import type { LocalizedString } from "@repo/shared/types";
import { type GoalTypeOption, suitabilityClientType } from "@repo/shared/types";
import { isComponentAllowedOnPageType } from "@/components/flow/flow-component-types";
import { groupAnsweringNotes } from "@/components/flow/group-answering-notes";
import type { CompletionMarkers } from "@/lib/hooks/use-flow-instances/use-flow-completion";
import { usePublicAnsweringSubjects } from "@/lib/hooks/use-public-flow/use-answering-subjects";
import type { GoalListConfig } from "@/lib/types/flow";
import {
  renderEmbed,
  renderFile,
  renderIframe,
  renderModal,
  renderSpacer,
  renderTextBlock,
  renderYoutubeVideo,
} from "@/pages/clients/detail/flows/instance/flow-component-renderers";
import {
  AssetClassForecast,
  GlobalAssetClassForecast,
  GlobalCostChart,
  GlobalCostTable,
  GlobalExpectedValue,
  GlobalProductCosts,
} from "@/pages/clients/detail/flows/instance/self-contained";
import { ClientClassificationRenderer } from "./flow/client-classification-renderer";
import { ClientPreferencesRenderer } from "./flow/client-preferences-renderer";
import { FieldRenderer } from "./flow/field-renderer";
import { GlobalRiskAssessmentRenderer } from "./flow/global-risk-assessment-renderer";
import { GoalInputRenderer } from "./flow/goal-input-renderer";
import { PublicGoalList } from "./flow/goal-list-renderer";
import { PortfolioCostsRenderer } from "./flow/portfolio-costs-renderer";
import { PublicPortfolioList } from "./flow/portfolio-list-renderer";
import { ProductAssessmentRenderer } from "./flow/product-assessment-renderer";
import { PublicImageRenderer } from "./flow/public-image-renderer";
import { QuizRenderer } from "./flow/quiz-renderer";
import { RiskAssessmentRenderer } from "./flow/risk-assessment-renderer";
import { SumsubVerificationRenderer } from "./flow/sumsub-verification-renderer";
import { SustainabilityRenderer } from "./flow/sustainability-renderer";
import type { GoalData } from "./flow/types";

export type PublicFlowComponentRendererProps = {
  component: any;
  isCompleted: boolean;
  language: string;
  flowInstanceId: string;
  riskModelId: string | null;
  portfolioCollectionId: string | null;
  flowCurrency: string;
  primaryColor?: string | null;
  productTypes: Array<{ id: string; nameI18n: LocalizedString }>;
  /** QP-187 — the flow collection's goal types (feeds the goal_input type picker). */
  goalTypes: GoalTypeOption[];
  /** QL-281 — whether the flow allows more than one goal (gates the goal list's add button). */
  multiGoal: boolean;
  /** QL-593 — the flow's goal cap while multiGoal is on. */
  maxGoals: number;
  portfolioCosts: any[];
  currentGoal: GoalData | null;
  currentGoalId: string | null;
  // QL-36 — a Group client can reach the public flow (public-flow-start-service
  // matches existing clients by email), so this is not a two-value union.
  clientType: "Person" | "Company" | "Group";
  clientName: string;
  chartColors?: string[] | null;
  onCustomPortfolioSaveComplete: () => Promise<void>;
  /**
   * Page scope (audit §7). On a "global" page, the instance-level components
   * (`expected_value`, `cost_table`, `cost_chart`, `product_costs`) render their
   * `Global*` aggregate variant; on a "goal" page they render nothing (the
   * placement policy keeps them off goal pages). `product_costs` has a
   * per-portfolio variant too, rendered by the portfolio page renderer (QL-417).
   */
  scope: "goal" | "global";
  /** D14 gating + engine markers (Follow-up C P2) for per-widget red borders. */
  showValidationErrors: boolean;
  markers: CompletionMarkers;
};

export function PublicFlowComponentRenderer({
  component,
  isCompleted,
  language,
  flowInstanceId,
  riskModelId,
  portfolioCollectionId,
  flowCurrency,
  primaryColor: _primaryColor,
  productTypes,
  goalTypes,
  multiGoal,
  maxGoals,
  portfolioCosts,
  currentGoal,
  currentGoalId,
  clientType,
  clientName,
  chartColors,
  onCustomPortfolioSaveComplete: _onCustomPortfolioSaveComplete,
  scope,
  showValidationErrors,
  markers,
}: PublicFlowComponentRendererProps) {
  const { componentType } = component;
  const isGlobal = scope === "global";
  // QL-188 — the group note on the components a group answers together.
  const { attribution } = usePublicAnsweringSubjects();
  const groupNotes = groupAnsweringNotes(clientType, attribution, language);

  // Guard: a component placed on a page type it is not allowed on (e.g. a
  // global-only component on a goal page, or vice versa) renders nothing in the
  // client-facing public flow — clients should never see a config-error notice.
  // Single source of truth: isComponentAllowedOnPageType.
  if (!isComponentAllowedOnPageType(componentType, scope)) return null;

  // Per-widget error (H2). Goal-page widgets key by `${goalId}:${componentId}`
  // (the definition is shared across goals); global-page widgets by componentId.
  // Mirrors the advisor's wizard-goal-page vs renderComponent split.
  const componentHasError =
    showValidationErrors &&
    (isGlobal
      ? markers.missingComponentIds.has(component.id)
      : markers.missingGoalComponentKeys.has(`${currentGoalId}:${component.id}`));

  // Parse component config once
  let config: Record<string, unknown> = {};
  try {
    config = component.config ? JSON.parse(component.config) : {};
  } catch {
    // Invalid config
  }

  // Field components — self-contained. Fields use `kind: "field"` markers
  // (missingFieldKeys), keyed `${goalId}:${fieldId}` for goal-scoped, else fieldId.
  if (componentType === "field" && component.flowField) {
    const fieldKey =
      component.flowField.scope === "goal" && currentGoalId
        ? `${currentGoalId}:${component.flowField.id}`
        : component.flowField.id;
    return (
      <FieldRenderer
        field={component.flowField}
        goalId={currentGoalId}
        isCompleted={isCompleted}
        hasValidationError={showValidationErrors && markers.missingFieldKeys.has(fieldKey)}
      />
    );
  }

  // Risk Assessment widget
  if (componentType === "risk_assessment") {
    // QL-212 — two placements, two rows. On a global page the client answers what
    // the risk model captures once for them, written to the instance-level row
    // and fanned out to every goal; on a goal page they answer what is genuinely
    // per goal. The global renderer deliberately takes NO goal id: the `goals[0]`
    // value this component receives on a global page is a stopgap for the other
    // widgets, and writing the client's single answer into the first goal's row
    // is exactly the mistake it would cause.
    return isGlobal ? (
      <GlobalRiskAssessmentRenderer
        component={component}
        riskModelId={riskModelId}
        flowInstanceId={flowInstanceId}
        isCompleted={isCompleted}
        language={language}
        hasValidationError={componentHasError}
        groupNote={groupNotes?.riskAssessment}
        missingReason={markers.missingComponentReasons.get(component.id)}
      />
    ) : (
      <RiskAssessmentRenderer
        component={component}
        riskModelId={riskModelId}
        currentGoalId={currentGoalId}
        currentGoal={currentGoal}
        isCompleted={isCompleted}
        language={language}
        hasValidationError={componentHasError}
        groupNote={groupNotes?.riskAssessment}
      />
    );
  }

  // QP-179 — sustainability preferences (global pages)
  if (componentType === "sustainability") {
    return (
      <SustainabilityRenderer
        component={component}
        isCompleted={isCompleted}
        language={language}
        hasValidationError={componentHasError}
        groupNote={groupNotes?.sustainability}
      />
    );
  }

  // QP-179 — sustainability_status is portfolio-scoped now (rendered by
  // PortfolioPageRenderer on the portfolio page); no case here.

  // QP-224 — client preference yes/no answers (global pages)
  if (componentType === "client_preferences") {
    return (
      <ClientPreferencesRenderer
        component={component}
        isCompleted={isCompleted}
        language={language}
        hasValidationError={componentHasError}
        groupNote={groupNotes?.clientPreferences}
      />
    );
  }

  // QL-13 — standalone knowledge quiz (global pages). Same widget and runner
  // as the advisor wizard, minus the override: a client cannot clear their own
  // gate.
  if (componentType === "quiz") {
    return (
      <QuizRenderer
        component={component}
        isCompleted={isCompleted}
        language={language}
        hasValidationError={componentHasError}
      />
    );
  }

  // Client Classification widget
  if (componentType === "client_classification") {
    return (
      <ClientClassificationRenderer
        component={component}
        // QL-36 — MiFID classification is Person/Company only, so a Group folds
        // to Person here. The advisor renderer already does this
        // (flow-instance-wizard), and widening `clientType` to include Group
        // surfaced that the public side was relying on the type being narrowed.
        clientType={suitabilityClientType(clientType)}
        clientName={clientName}
        isCompleted={isCompleted}
        hasValidationError={componentHasError}
      />
    );
  }

  // Product Assessment widget
  if (componentType === "product_assessment") {
    return (
      <ProductAssessmentRenderer
        component={component}
        portfolioCollectionId={portfolioCollectionId}
        productTypes={productTypes}
        isCompleted={isCompleted}
        hasValidationError={componentHasError}
      />
    );
  }

  // Static content components
  if (componentType === "text_block") return renderTextBlock(component);
  if (componentType === "spacer") return renderSpacer(component);
  if (componentType === "youtube_video") return renderYoutubeVideo(component);
  if (componentType === "iframe") return renderIframe(component);
  if (componentType === "image") return <PublicImageRenderer component={component} />;
  if (componentType === "embed") return renderEmbed(component);
  if (componentType === "modal") return renderModal(component, language);
  if (componentType === "file") return renderFile(component, language);

  // QP-150 — portfolio-scoped analytics (portfolio_display / portfolio_xray / asset_allocation /
  // historical_return / portfolio_forecast) and the per-portfolio editors render on the
  // portfolio page via PublicPortfolioPageRenderer, not here. Only the instance-aggregate variants
  // (Global*) render on global pages below.
  if (componentType === "expected_value") {
    return isGlobal ? (
      <GlobalExpectedValue
        instanceId={flowInstanceId}
        config={config as any}
        language={language}
        hasValidationError={componentHasError}
      />
    ) : null;
  }

  if (componentType === "cost_table") {
    return isGlobal ? (
      <GlobalCostTable
        instanceId={flowInstanceId}
        config={config}
        language={language}
        hasValidationError={componentHasError}
      />
    ) : null;
  }

  if (componentType === "cost_chart") {
    return isGlobal ? (
      <GlobalCostChart
        instanceId={flowInstanceId}
        config={config}
        language={language}
        hasValidationError={componentHasError}
      />
    ) : null;
  }

  // QL-315 — the asset-class forecast runs on both a global and a goal page.
  // The two read different snapshot rows: the goal one merges that goal's
  // portfolios, the global one every portfolio in the instance. Purely
  // informational, so no `hasValidationError` is threaded.
  if (componentType === "asset_class_forecast") {
    return isGlobal ? (
      <GlobalAssetClassForecast
        instanceId={flowInstanceId}
        config={config}
        language={language}
        chartColors={chartColors}
      />
    ) : (
      <AssetClassForecast
        goalId={currentGoalId}
        config={config}
        language={language}
        chartColors={chartColors}
      />
    );
  }

  // Product Costs — instance aggregate on global pages (per-portfolio variant is on the portfolio page).
  if (componentType === "product_costs") {
    return isGlobal ? (
      <GlobalProductCosts instanceId={flowInstanceId} config={config} language={language} />
    ) : null;
  }

  // Portfolio Costs (collection-level — not goal/instance scoped)
  if (componentType === "portfolio_costs") {
    return (
      <PortfolioCostsRenderer
        component={component}
        portfolioCosts={portfolioCosts}
        flowInstanceId={flowInstanceId}
        flowCurrency={flowCurrency}
        isCompleted={isCompleted}
        language={language}
      />
    );
  }

  // QP-172 — Portfolio List
  if (componentType === "portfolio_list") {
    return (
      <PublicPortfolioList
        goalId={isGlobal ? null : currentGoalId}
        config={config as any}
        language={language}
      />
    );
  }

  // QL-281 — Goal List (global pages)
  if (componentType === "goal_list") {
    return (
      <PublicGoalList
        flowInstanceId={flowInstanceId}
        goalTypes={goalTypes}
        config={config as GoalListConfig}
        language={language}
        isCompleted={isCompleted}
        multiGoal={multiGoal}
        groupNote={groupNotes?.goals}
        maxGoals={maxGoals}
      />
    );
  }

  // Goal Input widget
  if (componentType === "goal_input") {
    return (
      <GoalInputRenderer
        component={component}
        flowInstanceId={flowInstanceId}
        currentGoalId={currentGoalId}
        goalTypes={goalTypes}
        isCompleted={isCompleted}
        hasValidationError={componentHasError}
        groupNote={groupNotes?.goals}
      />
    );
  }

  // Custom Portfolio builder
  // QP-150 — custom_portfolio renders on the portfolio page via PublicPortfolioPageRenderer.

  // Sumsub Verification
  if (componentType === "sumsub_verification") {
    return (
      <SumsubVerificationRenderer
        component={component}
        isCompleted={isCompleted}
        language={language}
        hasValidationError={componentHasError}
      />
    );
  }

  return null;
}
